import { combineReducers } from 'redux';
import axios from 'axios';
import _ from 'lodash';
import i18next from 'i18next';
import { addNotification } from 'reapop';
import createNamedWrapperReducer from 'src/reducers/createNamedWrapperReducer';
import { listReducer, itemReducer } from 'src/reducers/common';
import { fetchList, fetchListSuccess, fetchListFailure, setExtras } from 'src/reducers/common/listReducer';
import { setGlobalSuccesses, setGlobalErrors } from 'src/reducers/global';
import { serializeHttpGetParams, redirectTo } from 'src/utils/Http';
import { takePaysSearchQueries } from 'src/reducers/searchForm';
import { DEFAULT_PAGING_PER_PAGE } from 'src/Config';
import {
  SEARCH_TAX_DEDUCTION_CERT_DATA,
  DOWNLOAD_TAX_DEDUCTION_CERT_DATA,
  BULK_UPDATE_TAX_DEDUCTION_CERTS,
  UNCONFIRMED_MONTHS_TAX_DEDUCTION_CERT_DATA,
  RESET_TAX_DEDUCTION_CERT_ADJUSTMENT
} from 'src/constants/EndpointUrls';

export const REDUCER_NAME = 'yearEndReportTaxDeductionCerts';

export const FETCH_UNCONFIRMED_MONTHS = `${REDUCER_NAME}/FETCH_UNCONFIRMED_MONTHS`;
export const FETCH_UNCONFIRMED_MONTHS_SUCCESS = `${REDUCER_NAME}/FETCH_UNCONFIRMED_MONTHS_SUCCESS`;
export const FETCH_UNCONFIRMED_MONTHS_FAILURE = `${REDUCER_NAME}/FETCH_UNCONFIRMED_MONTHS_FAILURE`;

export const fetchUnconfirmedMonths = (year, clientBranchId, employeeIds) => async dispatch => {
  if (!employeeIds || employeeIds.length === 0) return;
  try {
    dispatch({ type: FETCH_UNCONFIRMED_MONTHS });
    const fetchUrl = `${UNCONFIRMED_MONTHS_TAX_DEDUCTION_CERT_DATA}?${serializeHttpGetParams({
      year,
      client_branch_id: clientBranchId,
      employee_ids: employeeIds
    })}`;
    const response = await axios.get(fetchUrl);
    dispatch({ type: FETCH_UNCONFIRMED_MONTHS_SUCCESS, payload: response.data });
  } catch (exception) {
    dispatch({ type: FETCH_UNCONFIRMED_MONTHS_FAILURE, error: exception });
  }
};

export const fetchYearEndReportTaxDeductionCerts = (formValues, isFetchGrouped) => async dispatch => {
  try {
    dispatch(fetchList(REDUCER_NAME));
    let params = null;
    if (isFetchGrouped) {
      params = {
        year: formValues.groupedPaymentYears,
        client_branch_id: formValues.offices,
        page: formValues.page || 1,
        per_page: formValues.limit || DEFAULT_PAGING_PER_PAGE
      };
    } else {
      params = takePaysSearchQueries(formValues);
    }

    const fetchUrl = `${SEARCH_TAX_DEDUCTION_CERT_DATA}?${serializeHttpGetParams(params)}`;
    const response = await axios.get(fetchUrl);
    const { employeePays, currentPage, totalPages, totalCount } = response.data.payload;
    const payload = { data: employeePays, pageCount: totalPages, totalCount };
    dispatch(fetchListSuccess(REDUCER_NAME, payload));
    dispatch(
      setExtras(REDUCER_NAME, {
        currentPage
      })
    );
  } catch (exception) {
    dispatch(fetchListFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const bulkUpdateTaxDeductionCertAdjustments = (year, adjustments) => async dispatch => {
  try {
    const response = await axios.put(BULK_UPDATE_TAX_DEDUCTION_CERTS, { year, adjustments });
    dispatch(setGlobalSuccesses(i18next.t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.save_success')));
    return response;
  } catch (error) {
    dispatch(setGlobalErrors(i18next.t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.save_failure')));
    throw error;
  }
};

export const downloadTaxDeductionCerts = formValues => async () => {
  if (_.isUndefined(formValues?.groupedPaymentYears) || _.isUndefined(formValues?.offices)) {
    return;
  }

  const params = {
    year: formValues.groupedPaymentYears,
    client_branch_id: formValues.offices
  };
  const url = `${DOWNLOAD_TAX_DEDUCTION_CERT_DATA}?${serializeHttpGetParams(params)}`;
  redirectTo(url);
};

const initialUnconfirmedState = {
  data: {},
  loading: false
};

const unconfirmedMonthsReducer = (state = initialUnconfirmedState, action) => {
  switch (action.type) {
    case FETCH_UNCONFIRMED_MONTHS:
      return { ...state, loading: true };
    case FETCH_UNCONFIRMED_MONTHS_SUCCESS:
      return { ...state, loading: false, data: action.payload };
    case FETCH_UNCONFIRMED_MONTHS_FAILURE:
      return { ...state, loading: false };
    default:
      return state;
  }
};

export const resetTaxDeductionCertAdjustment = (year, employeeId, employeeName) => async dispatch => {
  try {
    const response = await axios.post(RESET_TAX_DEDUCTION_CERT_ADJUSTMENT, { year, employee_id: employeeId });
    dispatch(
      addNotification({
        title: i18next.t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.reset_modal.reset_success_title'),
        message: `<span class="reset-tax-deduction-cert-success">${i18next.t(
          'features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.reset_modal.reset_success_body',
          { employeeName }
        )}</span>`,
        position: 'tc',
        status: 'success',
        dismissAfter: 5000,
        allowHTML: true
      })
    );
    return response;
  } catch (error) {
    dispatch(setGlobalErrors(i18next.t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.reset_failure')));
    throw error;
  }
};

export default combineReducers({
  list: createNamedWrapperReducer(listReducer, REDUCER_NAME),
  item: createNamedWrapperReducer(itemReducer, REDUCER_NAME),
  unconfirmedMonths: unconfirmedMonthsReducer
});
